// Help Center orchestration module
//
// All help center content (categories, articles, search) is fetched via the unified
// /support/help/* API. The backend resolves the help center provider server-side
// (Gleap, Intercom, or Zendesk) via SUPPORT_HELP_CENTER_PROVIDER config.
//
// This is independent of the chat widget provider (VUE_APP_SUPPORT_SERVICE / VUE_APP_CP_SUPPORT_SERVICE)
// which controls which chat SDK is loaded (Gleap/Zendesk/Intercom).
//
// The unified API has a 2-level hierarchy (collection → articles) while the UI expects
// 3 levels (category → section → articles). Each collection acts as both category and section.

import dynStorage from '@/utils/storage-dynamic'

export default {
    state: {
		is_app_header_hidden: false,
		platform: 'web',
    },

    getters: {
		is_app_header_hidden: state => state.is_app_header_hidden,
		platform: state => state.platform,
    },

    actions: {
        // Returns categories list for the help center landing page
        GetHelpCenterCategories({ dispatch }) {
            return dispatch('GetUnifiedCollectionsList')
        },

        GetHelpCenterCategoryById({ dispatch }, category_id) {
            return dispatch('GetHelpCenterCategories').then(categories => {
                return categories.find( category => category.id === category_id )
            })
        },

        // Returns the full help center menu structure (sections with nested articles)
        // Builds 3-level UI structure from 2-level API: collection = category + section
        GetHelpCenterMenu({ dispatch }) {
            return dispatch('GetHelpCenterUnifiedMenu')
        },

        // Builds the help center menu from the unified API
        // Each collection becomes both a category and a section entry
        GetHelpCenterUnifiedMenu({ dispatch }) {
            return dispatch('GetUnifiedAllArticles').then(articles => {
                return dispatch('GetUnifiedCollectionsList').then(collections => [collections, articles])
            }).then(([collections, articles]) => {
                const menu = collections.map(({ id, name, description }) => ({
                    category_id: id,
                    id,
                    description: description || '',
                    name,
                }))

                articles.forEach(({ id, title, body, category_id, section_id, label_names }) => {
                    const menu_section = menu.find(section => section.id === section_id)

                    if (menu_section) {
                        if (!menu_section.articles) {
                            menu_section.articles = []
                        }

                        const article_category_name = collections.find(c => c.id === category_id)?.name || ''

                        menu_section.articles.push({
                            id,
                            category_id,
                            category_name: article_category_name,
                            section_id,
                            label_names,
                            title,
                            body,
                        })
                    }
                })

                return menu
            })
        },

        // Returns a section by ID (collection = section in unified API)
        GetHelpCenterSectionById({ dispatch }, section_id) {
            return dispatch('GetUnifiedCollectionsList').then(collections => {
                return collections.find(c => c.id === section_id)
            })
        },

        // Returns a single article by ID
        GetHelpCenterArticleById({ dispatch }, article_id) {
            return dispatch('GetUnifiedAllArticles').then(articles => {
                return articles.find(article => article.id === article_id)
            })
        },

        // Returns all articles belonging to a section (collection)
        GetHelpCenterArticlesBySectionId({ dispatch }, section_id) {
            return dispatch('GetUnifiedArticlesByCollectionId', section_id)
        },

        // Searches articles across the help center
        // Uses unified API search endpoint; falls back to client-side filtering
        FindHelpCenterArticles({ dispatch }, search_word) {
            if (!search_word) {
                return Promise.resolve([])
            }

            return dispatch('FindUnifiedArticles', search_word).then(result_ids => {
                return dispatch('GetHelpCenterMenuArticlesList').then(articles => {
                    return articles.filter(({id}) => result_ids.includes(id))
                })
            })
        },

        GetHelpCenterMenuArticlesList({ dispatch }){
            return dispatch('GetHelpCenterMenu').then(menu => {
                const helpCenterMenuArticlesList = []

                if (!menu) return helpCenterMenuArticlesList

                menu.forEach(({articles}) => {
                    if (articles) {
                        articles.forEach(article => {
                            helpCenterMenuArticlesList.push(article)
                        })
                    }
                })

                return helpCenterMenuArticlesList
            })
        },

		SetIsAppHeaderHidden({ commit }, is_hidden) {
			commit('SET_IS_APP_HEADER_HIDDEN', is_hidden)
		},

		SetPlatform({ commit }, platform) {
			commit('SET_PLATFORM', platform)
		},
    },

    mutations: {
        SET_IS_APP_HEADER_HIDDEN(state, is_hidden) {
            state.is_app_header_hidden = is_hidden

            dynStorage.set('is_app_header_hidden', is_hidden, null, true);
        },

        SET_PLATFORM(state, platform) {
            state.platform = platform

            dynStorage.set('platform', platform, null, false);
        },
    }
}
